import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  emptyTopicPart,
  TopicPart,
  TopicPartFormData,
} from "../../core/topicPart/entities/TopicPart";
import FormLayout from "../components/layout/FormLayout";
import { useTopicPartFormValidation } from "../../core/hooks/form/useTopicPartFormValidation";
import topicPartFormConfig from "./config/topicPart/topicPartFormConfig";
import TopicPartQuestionList from "./parts/TopicPartQuestionList";
import { useTrainingRoute } from "../../core/hooks/useTrainingRoute";
import { useAdminPermissions } from "../../core/hooks/useAdminPermissions";
import Form from "../components/form/Form";
import { getTopicPartByIdUseCase } from "../../core/topicPart/useCases/getTopicPartByIdUseCase";
import { addUpdateTopicPartUseCase } from "../../core/topicPart/useCases/addUpdateTopicPartUseCase";
import { deleteTopicPartUseCase } from "../../core/topicPart/useCases/deleteTopicPartUseCase";
import { copyTopicPartContentUseCase } from "../../core/topicPart/useCases/copyTopicPartContentUseCase";
import { downloadTopicPartContentUseCase } from "../../core/topicPart/useCases/downloadTopicPartContentUseCase";
import Loader from "../components/utils/Loader";
import { Shutter } from "../components/shutter/Shutter";
import { useConfirmModal } from "../../core/hooks/useConfirmModal";

export default function TopicPartPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { topicPartId, topicId, isNew, topicPath, topicPartPath } =
    useTrainingRoute();
  const { showModal, ConfirmModalComponent } = useConfirmModal();
  const permissions = useAdminPermissions();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["topicPart", topicPartId],
    enabled: Boolean(topicId),
    queryFn: async () => {
      if (!topicPartId || isNew)
        return emptyTopicPart(topicId ?? "") as TopicPart;
      return await getTopicPartByIdUseCase.exec(topicPartId);
    },
  });

  const form = useTopicPartFormValidation(data ?? null, t);
  const { isDirty } = form.formState;

  const closePanel = useCallback(() => {
    navigate(topicPath());
  }, [navigate, topicPath]);

  const requestClose = useCallback(() => {
    if (isDirty) {
      showModal(t("modal.unsavedTitle"), t("modal.unsavedMessage"), closePanel);
    } else {
      closePanel();
    }
  }, [isDirty, showModal, t, closePanel]);

  if (!topicId) {
    return null;
  }

  return (
    <>
      <Shutter
        onClose={requestClose}
        closeLabel={t("common.closePanel")}
        resizeAriaLabel={t("common.resizePanel")}
        initialWidthPercent={65}
        minWidthPercent={30}
      >
        {isLoading ? (
          <div className="flex h-[50dvh] items-center justify-center">
            <Loader className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div className="pb-[200px] p-1 md:p-4 space-y-8">
            <FormLayout
              title={isNew ? t("topicPart.new") : data?.title ?? ""}
              onCopy={
                permissions.role === "admin" && data
                  ? async () => copyTopicPartContentUseCase.exec(data)
                  : undefined
              }
              onDownload={
                permissions.role === "admin" && data
                  ? () => downloadTopicPartContentUseCase.exec(data)
                  : undefined
              }
              history={
                !isNew && data?.id
                  ? {
                      collection: "topicParts",
                      recordId: data.id,
                      recordLabel: data.title,
                      onRestored: () => void refetch(),
                    }
                  : undefined
              }
            >
              {!!form && (
                <Form
                  id={data?.id}
                  onSave={async (
                    data: TopicPartFormData,
                    id: string | number | null
                  ) => {
                    const topicPart = await addUpdateTopicPartUseCase.exec(
                      data as TopicPartFormData,
                      id
                    );
                    if (!topicPart) return;

                    if (id) {
                      refetch();
                    } else {
                      navigate(topicPartPath(topicPart.id), { replace: true });
                    }
                  }}
                  onDelete={async (id: string | number) => {
                    const success = await deleteTopicPartUseCase.exec(id);
                    if (success) closePanel();
                  }}
                  form={form!}
                  formConfig={topicPartFormConfig}
                />
              )}
            </FormLayout>
            {!isNew && topicPartId && (
              <TopicPartQuestionList topicPartId={topicPartId} />
            )}
            <div className="mb-50"></div>
          </div>
        )}
      </Shutter>
      <ConfirmModalComponent />
    </>
  );
}
